'use client';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { MediaType, ScreenType } from '@/helpers/enums';
import { useGetCommonStore } from '@/store';
import { useCurrentMediaType, useCurrentScreenType } from './hooks';

export interface IResponsiveContext {
  currentMediaType: MediaType;
  currentScreenType: ScreenType;
}

const ResponsiveContext = createContext<IResponsiveContext | null>(null);

export function ResponsiveProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const currentMediaType = useCurrentMediaType();
  const currentScreenType = useCurrentScreenType();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const defaultMediaType = isMobileDevice
    ? MediaType.MOBILE_S
    : MediaType.DESKTOP;

  const defaultScreenType = isMobileDevice ? ScreenType.BASE : ScreenType.XL;

  const contextValue = useMemo(
    () => ({
      currentMediaType: currentMediaType ?? defaultMediaType,
      currentScreenType: currentScreenType ?? defaultScreenType,
    }),
    [currentMediaType, defaultMediaType, currentScreenType, defaultScreenType]
  );

  return (
    <ResponsiveContext.Provider value={contextValue}>
      {children}
    </ResponsiveContext.Provider>
  );
}

export function useResponsive() {
  const resposiveContext = useContext(ResponsiveContext);

  if (!resposiveContext) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }

  return resposiveContext;
}
